import React from 'react';
import Button from '../../components/buttons/Button';
import Card from '../../components/Card/Card';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import InfoBanner from '../../components/InfoBanner/InfoBanner';
import Picker from '../../components/inputs/Picker';
import { FaDownload, FaTrash, FaUpload, FaFileWord, FaImage, FaFileUpload, FaLightbulb } from 'react-icons/fa';
import { formatReportTypeDisplayName } from '../../models/ReportTypeDesignHelper';
import { wordService } from '../../services';
import '../../Templates/Templates.css';

const TemplatesTab = ({
  enterpriseId,
  enterpriseTemplates,
  enterpriseTemplateLoading,
  enterpriseTemplateError,
  availableReportTypes,
  uploadEnterpriseTemplate,
  handleTemplateFileSelected,
  deleteEnterpriseTemplate,
  setEnterpriseTemplateReportType,
  downloadEnterpriseTemplate,
  showReportTypeModal,
  selectedReportTypeForUpload,
  setSelectedReportTypeForUpload,
  confirmTemplateUpload,
  cancelTemplateUpload,
  reportTypes,
  enterpriseDefaultTemplate,
  enterpriseDefaultTemplateLoading,
  enterpriseDefaultTemplateError,
  enterpriseDefaultTemplateFileInputRef,
  uploadEnterpriseDefaultTemplate,
  deleteEnterpriseDefaultTemplate,
  exampleTemplates,
  exampleTemplatesLoading,
  selectedExampleId,
  setSelectedExampleId,
  downloadExampleTemplate,
  currentProfile
}) => {
  const [logoUrl, setLogoUrl] = React.useState('');
  const [logoLoading, setLogoLoading] = React.useState(false);
  const [logoError, setLogoError] = React.useState('');
  const fileInputRef = React.useRef(null);
  const templateInputRef = React.useRef(null);
  const logoUrlRef = React.useRef('');

  const loadEnterpriseLogo = React.useCallback(async () => {
    if (!enterpriseId) return;
    setLogoLoading(true);
    setLogoError('');
    try {
      const blob = await wordService.getEnterpriseLogo(enterpriseId);
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (e) {}
        logoUrlRef.current = '';
      }
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        logoUrlRef.current = url;
        setLogoUrl(url);
      } else {
        setLogoUrl('');
      }
    } catch (e) {
      // If not found or other error, just clear the logo
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (err) {}
        logoUrlRef.current = '';
      }
      setLogoUrl('');
    } finally {
      setLogoLoading(false);
    }
  }, [enterpriseId]);

  React.useEffect(() => {
    loadEnterpriseLogo();
    return () => {
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (e) {}
        logoUrlRef.current = '';
      }
    };
  }, [enterpriseId, loadEnterpriseLogo]);

  const handleUploadLogo = async (file) => {
    if (!file) return;
    setLogoError('');
    // Validate type and size (<= 5MB)
    if (!file.type.startsWith('image/')) {
      setLogoError('Le fichier doit être une image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('Taille maximale 5 Mo.');
      return;
    }
    setLogoLoading(true);
    try {
      await wordService.uploadEnterpriseLogo(enterpriseId, file);
      await loadEnterpriseLogo();
    } catch (e) {
      setLogoError("Échec du téléchargement du logo.");
    } finally {
      setLogoLoading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!enterpriseId) return;
    if (!window.confirm('Supprimer le logo de l\'organisation ?')) return;
    setLogoLoading(true);
    setLogoError('');
    try {
      await wordService.deleteEnterpriseLogo(enterpriseId);
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (e) {}
        logoUrlRef.current = '';
      }
      setLogoUrl('');
    } catch (e) {
      setLogoError('Échec de la suppression du logo.');
    } finally {
      setLogoLoading(false);
    }
  };

  return (
    <div className="templates-container">
      {enterpriseTemplateError && (
        <div className="enterprise-admin-template-error" style={{ marginBottom: 12 }}>
          {enterpriseTemplateError}
        </div>
      )}

      {/* Enterprise Default Template */}
      <Card className="card-large">
        <SectionHeader icon={FaFileWord} title="Template Word par défaut de l'organisation"  />
        <InfoBanner icon={FaLightbulb} title="À propos du template par défaut de l'organisation">
          Le template par défaut de l'organisation est utilisé pour tous les rapports de l'organisation qui n'ont pas de template spécifique associé. <br />
          Créez un document Word (.docx) avec votre entête, logos, signature, etc. et incluez le placeholder <strong>{'{rapport}'}</strong> à l'endroit où le rapport doit être inséré.
        </InfoBanner>

        {enterpriseDefaultTemplate ? (
          <div className="template-default-info">
            <p><strong>Template actuel :</strong> {enterpriseDefaultTemplate.name}</p>
            <p><strong>Téléchargé le :</strong> {new Date(enterpriseDefaultTemplate.createdAt).toLocaleDateString('fr-FR')}</p>
            <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
              <input
                ref={enterpriseDefaultTemplateFileInputRef}
                type="file"
                accept=".docx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) {
                    uploadEnterpriseDefaultTemplate(file);
                    e.target.value = '';
                  }
                }}
                disabled={enterpriseDefaultTemplateLoading}
              />
              <Button
                variant="primary"
                icon={FaFileUpload}
                onClick={() => enterpriseDefaultTemplateFileInputRef.current && enterpriseDefaultTemplateFileInputRef.current.click()}
                disabled={enterpriseDefaultTemplateLoading}
              >
                Remplacer le template
              </Button>
              <Button
                variant="danger"
                icon={FaTrash}
                onClick={deleteEnterpriseDefaultTemplate}
                disabled={enterpriseDefaultTemplateLoading}
              >
                Supprimer
              </Button>
            </div>
          </div>
        ) : (
          <div className="template-default-empty">
            <p>Aucun template par défaut configuré pour l'organisation.</p>
            <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <input
                ref={enterpriseDefaultTemplateFileInputRef}
                type="file"
                accept=".docx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) {
                    uploadEnterpriseDefaultTemplate(file);
                    e.target.value = '';
                  }
                }}
                disabled={enterpriseDefaultTemplateLoading}
              />
              <Button
                variant="primary"
                icon={FaFileUpload}
                onClick={() => enterpriseDefaultTemplateFileInputRef.current && enterpriseDefaultTemplateFileInputRef.current.click()}
                disabled={enterpriseDefaultTemplateLoading}
              >
                Ajouter un template par défaut
              </Button>
            </div>
          </div>
        )}

        {enterpriseDefaultTemplateError && (
          <div className="enterprise-admin-template-error" style={{ marginTop: 8 }}>{enterpriseDefaultTemplateError}</div>
        )}

      </Card>

      {/* Organization Logo Card */}
      <Card className="card-large">
        <SectionHeader icon={FaImage} title="Logo de l'organisation" variant="default" />
        <InfoBanner>
          Ce logo apparaît dans l'en-tête de vos rapports exportés, à l'emplacement « logoSection » si présent.
        </InfoBanner>
        <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{
            width: 140,
            height: 80,
            border: '1px solid #ddd',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fafafa',
            overflow: 'hidden'
          }}>
            {logoLoading ? (
              <span>Chargement…</span>
            ) : logoUrl ? (
              <img src={logoUrl} alt="Logo organisation" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <span style={{ color: '#888', fontSize: 12 }}>Aucun logo</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) handleUploadLogo(file);
              e.target.value = '';
            }}
            disabled={logoLoading}
          />
          <Button
            variant="primary"
            icon={FaUpload}
            disabled={logoLoading}
            onClick={() => { try { fileInputRef.current && fileInputRef.current.click(); } catch (e) {} }}
          >
            {logoUrl ? 'Remplacer le logo' : 'Ajouter un logo'}
          </Button>
          <Button
            variant="danger"
            icon={FaTrash}
            onClick={handleDeleteLogo}
            disabled={logoLoading || !logoUrl}
          >
            Supprimer
          </Button>
        </div>
        {logoError && (
          <div className="enterprise-admin-template-error" style={{ marginTop: 8 }}>{logoError}</div>
        )}
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>Formats images uniquement (.png, .jpg, .jpeg), taille maximale 5 Mo. Un seul logo à la fois.</p>
      </Card>

      {/* Organization Templates Card */}
      <Card className="card-large">
        <SectionHeader icon={FaFileWord} title={`Templates Word spécifiques de l'organisation (${enterpriseTemplates.length}/10)`} variant="default" />
        <InfoBanner icon={FaLightbulb} title="Télécharger un nouveau template">
          Les tempates spécifiques sont liés à un modèle de rapport. Préparez un document Word (.docx) avec vos styles (titres, paragraphes, en-têtes/pieds) et uploadez le ici.
        </InfoBanner>

        <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            ref={templateInputRef}
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) {
                handleTemplateFileSelected(file);
              }
              e.target.value = '';
            }}
            disabled={enterpriseTemplateLoading || enterpriseTemplates.length >= 10}
          />
          <Button
            variant="primary"
            icon={FaUpload}
            onClick={() => { try { templateInputRef.current && templateInputRef.current.click(); } catch (e) {} }}
            disabled={enterpriseTemplateLoading || enterpriseTemplates.length >= 10}
          >
            Uploader un template
          </Button>
          {enterpriseTemplates.length >= 10 && (
            <p className="template-limit-warning">
              Limite de 10 templates atteinte. Supprimez un template existant pour en ajouter un nouveau.
            </p>
          )}
        </div>

        <div className="template-list-section">
          {enterpriseTemplates.length === 0 ? (
            <p className="template-empty-message">
              Aucun template personnalisé. Téléchargez un template exemple ou créez le vôtre pour une personnalisation avancée.
            </p>
          ) : (
            <div className="template-list">
              {enterpriseTemplates.map((template) => (
                <div key={template.id} className="template-item">
                  <span className="template-name">{template.name}</span>
                  <div className="template-controls">
                    <div className="template-associated-label">
                      Modèle associé:
                    </div>
                    <select
                      value={template.reportTypeId || (template.reportType ? (availableReportTypes.find(t => t.name === template.reportType)?.id || '') : (availableReportTypes.length > 0 ? availableReportTypes[0].id : ''))}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selected = availableReportTypes.find(t => String(t.id) === String(selectedId));
                        setEnterpriseTemplateReportType(template, selected?.name || null, selected?.id || null);
                      }}
                      className="template-report-type-select"
                      disabled={enterpriseTemplateLoading}
                    >
                      {availableReportTypes.map((type, index) => (
                        <option key={`template-${template.id}-type-${type.id || index}`} value={type.id}>
                          {formatReportTypeDisplayName(type.name)}
                        </option>
                      ))}
                    </select>
                    <div className="template-actions">
                      <Button
                        variant="info"
                        icon={FaDownload}
                        onClick={() => downloadEnterpriseTemplate(template)}
                        disabled={enterpriseTemplateLoading}
                        title="Télécharger"
                      />
                      {template.id !== '00000000-0000-0000-0000-000000000001' && (
                        <Button
                          variant="danger"
                          icon={FaTrash}
                          onClick={() => {
                            if (window.confirm('Êtes-vous sûr de vouloir supprimer ce template?')) {
                              deleteEnterpriseTemplate(template.id);
                            }
                          }}
                          disabled={enterpriseTemplateLoading}
                          title="Supprimer"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Example Templates Card */}
      <Card className="card-large">
        <SectionHeader icon={FaFileWord} title="Templates exemples" variant="default" />
        <InfoBanner>
          Téléchargez un template exemple, adaptez la mise en page dans Word, puis uploadez votre version personnalisée dans la section « Templates de l'organisation ».
        </InfoBanner>
        <div className="template-system-section">
          <p>Sélectionnez et téléchargez un template exemple pour l'utiliser comme base.</p>
          {exampleTemplatesLoading ? (
            <div className="template-loading">
              <div className="spinner"></div>
              <p>Chargement des templates exemples...</p>
            </div>
          ) : exampleTemplates.length > 0 ? (
            <div className="template-example-list">
              <select
                value={selectedExampleId}
                onChange={(e) => setSelectedExampleId(e.target.value)}
                className="template-example-select"
                disabled={enterpriseTemplateLoading}
              >
                <option value="">-- Sélectionnez un template --</option>
                {exampleTemplates.map((example) => (
                  <option key={example.id} value={example.id}>
                    Template {formatReportTypeDisplayName(example.reportType)}
                  </option>
                ))}
              </select>
              <Button
                variant="primary"
                icon={FaDownload}
                size="small"
                onClick={downloadExampleTemplate}
                disabled={!selectedExampleId || enterpriseTemplateLoading}
              >
                Télécharger 
              </Button>
            </div>
          ) : (
            <div className="template-example-empty">
              <p>Aucun template d'exemple n'est disponible actuellement.</p>
            </div>
          )}
        </div>
      </Card>

      {/* Report Type Selection Modal */}
      {showReportTypeModal && (() => {
        // Filter out report types that already have templates
        const usedReportTypeIds = enterpriseTemplates.map(t => t.reportTypeId).filter(Boolean);
        const availableTypes = reportTypes ? reportTypes.filter(t => !usedReportTypeIds.includes(t.id)) : [];

        return (
          <div className="template-modal-overlay" onClick={cancelTemplateUpload}>
            <div className="template-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="template-modal-title">Associer à un type de rapport</h3>
              <p className="template-modal-description">
                Sélectionnez le type de rapport pour lequel ce template sera utilisé.
              </p>

              <Picker
                id="enterprise-report-type-upload-picker"
                value={selectedReportTypeForUpload}
                onChange={(e) => setSelectedReportTypeForUpload(e.target.value)}
                disabled={enterpriseTemplateLoading}
              >
                {availableTypes.map((type, index) => (
                  <option key={`report-type-${type.id || index}`} value={type.id}>
                    {formatReportTypeDisplayName(type.name)}
                  </option>
                ))}
              </Picker>

              <div className="template-modal-actions">
                <Button
                  variant="secondary"
                  onClick={cancelTemplateUpload}
                  disabled={enterpriseTemplateLoading}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  icon={FaFileUpload}
                  onClick={confirmTemplateUpload}
                  disabled={enterpriseTemplateLoading}
                >
                  {enterpriseTemplateLoading ? 'Téléchargement...' : 'Uploader'}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default TemplatesTab; 